/**
 * PATH: apps/hr/src/pages/employees/employees-list/EmployeeRow.tsx
 * PURPOSE: Table row component for the employee directory
 * WHY: Extracted from EmployeesPage.tsx to keep files under 300 lines
 */

import { useState } from 'react'
import {
  Mail, Phone, MoreVertical, UserCheck, Building2, MapPin,
  UserPlus, KeyRound,
} from 'lucide-react'
import { cn, formatDate, getInitials, formatStatus } from '../../../lib/utils'
import type { Employee, EmploymentType, EmployeeStatus } from '../../../types'

interface EmployeeRowProps {
  employee: Employee
  onClick: () => void
  onEnableLogin: (employee: Employee) => void
  isEnablingLogin?: boolean
}

const statusColors: Record<EmployeeStatus, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  on_leave: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  inactive: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  terminated: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

const typeLabels: Record<EmploymentType, string> = {
  full_time: 'Full Time',
  part_time: 'Part Time',
  casual: 'Casual',
  contractor: 'Contractor',
}

export function EmployeeRow({ employee, onClick, onEnableLogin, isEnablingLogin }: EmployeeRowProps) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <tr onClick={onClick} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors border-b border-slate-100 dark:border-slate-800 last:border-0">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-sm font-medium shrink-0">
            {getInitials(`${employee.firstName} ${employee.lastName}`)}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{employee.firstName} {employee.lastName}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{employee.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400"><Building2 className="w-3.5 h-3.5" />{employee.department}</div>
      </td>
      <td className="px-4 py-3 hidden lg:table-cell">
        <p className="text-sm text-slate-600 dark:text-slate-400">{employee.position}</p>
      </td>
      <td className="px-4 py-3 hidden xl:table-cell">
        <span className="text-xs text-slate-500 dark:text-slate-400">{typeLabels[employee.employmentType]}</span>
      </td>
      <td className="px-4 py-3">
        <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', statusColors[employee.status])}>{formatStatus(employee.status)}</span>
      </td>
      <td className="px-4 py-3 hidden lg:table-cell">
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400"><MapPin className="w-3.5 h-3.5" />{employee.location || '-'}</div>
      </td>
      <td className="px-4 py-3 hidden xl:table-cell">
        <span className="text-sm text-slate-500 dark:text-slate-400">{employee.hireDate ? formatDate(employee.hireDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end">
          <div className="relative">
            <button onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <MoreVertical className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            </button>
            {menuOpen && (<>
              <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setMenuOpen(false) }} />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-20">
                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onClick() }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><UserCheck className="w-4 h-4 text-slate-400" />View Profile</button>
                {employee.email && <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); window.location.href = `mailto:${employee.email}` }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Mail className="w-4 h-4 text-slate-400" />Send Email</button>}
                {employee.phone && <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); window.location.href = `tel:${employee.phone}` }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Phone className="w-4 h-4 text-slate-400" />Call {employee.phone}</button>}
                <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
                {employee.email && !employee.user_id && <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEnableLogin(employee) }} disabled={isEnablingLogin} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors disabled:opacity-50"><UserPlus className="w-4 h-4" />Enable Login</button>}
                {employee.user_id && <div className="flex items-center gap-2.5 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400"><KeyRound className="w-4 h-4" />Login Enabled</div>}
              </div>
            </>)}
          </div>
        </div>
      </td>
    </tr>
  )
}
